import { FormEvent, useEffect, useState } from "react";
import { AlertTriangle, CalendarClock, CheckCircle2, ClipboardList, Filter, RefreshCw, Search, Store } from "lucide-react";
import { useNavigate } from "react-router-dom";
import AdminLayout from "./AdminLayout";
import { AdminApiError, AdminStoreOverviewLocationSummary, AdminStoreOverviewSummary, AdminStoreUserLocation, AdminUser, getAdminStoreOverview, getCurrentAdmin, logout } from "./api";

const year = new Date().getFullYear();
const defaultFrom = `${year}-01-01`;
const defaultTo = `${year}-12-31`;
const emptySummary: AdminStoreOverviewSummary = { Locations: 0, NewReservations: 0, ServiceRecords: 0, VoidedServiceRecords: 0, OrdersPending: 0, OrdersReady: 0, OrdersCompleted: 0, FollowUpsOverdue: 0, FollowUpsToday: 0, ScheduleWarnings: 0 };

export default function AdminStoreOverview() {
  const navigate = useNavigate();
  const [currentUser, setCurrentUser] = useState<AdminUser | null>(null);
  const [locations, setLocations] = useState<AdminStoreUserLocation[]>([]);
  const [rows, setRows] = useState<AdminStoreOverviewLocationSummary[]>([]);
  const [summary, setSummary] = useState<AdminStoreOverviewSummary>(emptySummary);
  const [locationIds, setLocationIds] = useState<string[]>([]);
  const [dateFrom, setDateFrom] = useState(defaultFrom);
  const [dateTo, setDateTo] = useState(defaultTo);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState("");

  async function load(filters = { DateFrom: dateFrom, DateTo: dateTo, LocationIds: locationIds }) {
    setLoading(true);
    setMessage("");
    try {
      const me = await getCurrentAdmin();
      setCurrentUser(me.User);
      const data = await getAdminStoreOverview(filters);
      setLocations(data.Locations || []);
      setRows(data.LocationsSummary || []);
      setSummary(data.Summary || emptySummary);
      setDateFrom(data.DateFrom || filters.DateFrom || defaultFrom);
      setDateTo(data.DateTo || filters.DateTo || defaultTo);
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "門市總覽載入失敗。");
      if (error instanceof AdminApiError && error.status === 401) navigate("/admin-login", { replace: true });
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load({ DateFrom: defaultFrom, DateTo: defaultTo, LocationIds: [] });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function handleLogout() {
    await logout().catch(() => undefined);
    navigate("/admin-login", { replace: true });
  }

  function handleSearch(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    load({ DateFrom: dateFrom, DateTo: dateTo, LocationIds: locationIds });
  }

  function resetFilters() {
    setDateFrom(defaultFrom);
    setDateTo(defaultTo);
    setLocationIds([]);
    load({ DateFrom: defaultFrom, DateTo: defaultTo, LocationIds: [] });
  }

  function toggleLocation(id: string) {
    setLocationIds((current) => (current.includes(id) ? current.filter((item) => item !== id) : [...current, id]));
  }

  return (
    <AdminLayout sectionLabel="門市管理" title="門市總覽" user={currentUser} onLogout={handleLogout}>
      <form className="mb-5 rounded-md border border-[#dfe6e1] bg-white p-3 2xl:p-4" onSubmit={handleSearch}>
        <div className="grid gap-3 sm:grid-cols-2 2xl:grid-cols-[190px_190px_auto] 2xl:items-end">
          <Field label="起日"><input className="h-11 w-full rounded-md border border-[#cfd8d2] px-3 outline-none focus:border-[#315746] focus:ring-2 focus:ring-[#315746]/15" type="date" value={dateFrom} onChange={(event) => setDateFrom(event.target.value)} /></Field>
          <Field label="迄日"><input className="h-11 w-full rounded-md border border-[#cfd8d2] px-3 outline-none focus:border-[#315746] focus:ring-2 focus:ring-[#315746]/15" type="date" value={dateTo} onChange={(event) => setDateTo(event.target.value)} /></Field>
          <div className="rwd-action-grid sm:col-span-2 2xl:col-span-1">
            <button className="inline-flex h-11 items-center justify-center gap-2 rounded-md bg-[#315746] px-4 font-bold text-white hover:bg-[#203d32]" type="submit"><Search size={18} />查詢</button>
            <button className="inline-flex h-11 items-center justify-center gap-2 rounded-md border border-[#cfd8d2] bg-white px-4 font-bold text-[#315746] hover:bg-[#f0f5f2]" onClick={resetFilters} type="button"><RefreshCw size={18} />重設</button>
          </div>
        </div>
        <LocationFilter locations={locations} selected={locationIds} onToggle={toggleLocation} onAll={() => setLocationIds(locations.map((item) => item.LocationId))} onClear={() => setLocationIds([])} />
      </form>

      <div className="mb-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-5">
        <SummaryCard icon={<Store size={18} />} label="門市數" value={summary.Locations} />
        <SummaryCard icon={<CalendarClock size={18} />} label="新預約" value={summary.NewReservations} />
        <SummaryCard icon={<ClipboardList size={18} />} label="服務紀錄" value={summary.ServiceRecords} />
        <SummaryCard icon={<AlertTriangle size={18} />} label="逾期回訪" value={summary.FollowUpsOverdue} />
        <SummaryCard icon={<CheckCircle2 size={18} />} label="已交付訂單" value={summary.OrdersCompleted} />
      </div>

      {message && <div className="mb-4 rounded-md border border-[#f2b8b5] bg-[#fdebea] px-4 py-3 text-sm font-medium text-[#8a1f1f]">{message}</div>}

      <section className="rounded-md border border-[#dfe6e1] bg-white">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[1060px] border-collapse text-left">
            <thead className="bg-[#f0f3f1] text-sm text-[#59665f]">
              <tr>
                <th className="px-4 py-3 font-bold">門市</th>
                <th className="px-4 py-3 font-bold">預約</th>
                <th className="px-4 py-3 font-bold">服務紀錄</th>
                <th className="px-4 py-3 font-bold">門市訂單</th>
                <th className="px-4 py-3 font-bold">回訪</th>
                <th className="px-4 py-3 font-bold">時段覆蓋</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td className="px-4 py-8 text-center text-[#7b877f]" colSpan={6}>載入中...</td></tr>
              ) : rows.length === 0 ? (
                <tr><td className="px-4 py-8 text-center text-[#7b877f]" colSpan={6}>目前沒有符合條件的門市資料。</td></tr>
              ) : rows.map((row) => (
                <tr className="border-t border-[#edf0ee] align-top hover:bg-[#fbfcfb]" key={row.LocationId}>
                  <td className="px-4 py-4 font-extrabold text-[#203d32]">{row.LocationName}</td>
                  <td className="px-4 py-4 text-sm leading-6 text-[#59665f]">今日 {row.TodayReservations}<br />新預約 {row.NewReservations}</td>
                  <td className="px-4 py-4 text-sm leading-6 text-[#59665f]">有效 {row.ServiceRecords}<br />作廢 {row.VoidedServiceRecords}</td>
                  <td className="px-4 py-4 text-sm leading-6 text-[#59665f]">待處理 {row.OrdersPending}<br />到店/可取 {row.OrdersReady}<br />已交付 {row.OrdersCompleted}</td>
                  <td className="px-4 py-4 text-sm leading-6 text-[#59665f]">逾期 {row.FollowUpsOverdue}<br />今日 {row.FollowUpsToday}</td>
                  <td className="px-4 py-4 text-sm font-bold text-[#203d32]">{row.ScheduleConfiguredThrough || "尚未設定"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </AdminLayout>
  );
}

function Field({ children, label }: { children: React.ReactNode; label: string }) {
  return <label className="grid min-w-0 gap-1 text-sm font-bold text-[#59665f]"><span>{label}</span>{children}</label>;
}

function SummaryCard({ icon, label, value }: { icon: React.ReactNode; label: string; value: number }) {
  return <div className="rounded-md border border-[#dfe6e1] bg-white p-4"><div className="flex items-center justify-between gap-3"><p className="text-sm font-bold text-[#7b877f]">{label}</p><span className="text-[#315746]">{icon}</span></div><p className="mt-3 text-3xl font-extrabold text-[#203d32]">{value || 0}</p></div>;
}

function LocationFilter({ locations, selected, onToggle, onAll, onClear }: { locations: AdminStoreUserLocation[]; selected: string[]; onToggle: (id: string) => void; onAll: () => void; onClear: () => void }) {
  return <div className="mt-4 border-t border-[#edf0ee] pt-3"><div className="mb-2 grid gap-2 sm:flex sm:flex-wrap sm:items-center sm:justify-between"><label className="inline-flex items-center gap-2 text-sm font-bold text-[#59665f]"><Filter size={15} />門市篩選</label><div className="flex flex-wrap items-center gap-2 text-xs font-bold text-[#315746]"><span className="text-[#7b877f]">已選 {selected.length} 間</span><button className="hover:underline" onClick={onAll} type="button">全選</button><span className="text-[#c9d2cc]">|</span><button className="hover:underline" onClick={onClear} type="button">清除</button></div></div><div className="flex gap-2 overflow-x-auto pb-1 sm:flex-wrap sm:overflow-visible">{locations.map((location) => { const checked = selected.includes(location.LocationId); return <label className={`flex h-10 shrink-0 cursor-pointer items-center gap-2 rounded-md border px-3 text-sm font-bold ${checked ? "border-[#9ac8aa] bg-[#f1f8f3] text-[#245438]" : "border-[#d8dedb] bg-white text-[#59665f]"}`} key={location.LocationId}><input checked={checked} className="h-4 w-4 accent-[#315746]" onChange={() => onToggle(location.LocationId)} type="checkbox" />{location.Name}</label>; })}</div></div>;
}
